// Piezas comunes a las dos tablas de líneas.
//
// Los dos grupos —pendiente y facturado— tienen juegos de columnas DISTINTOS a
// propósito: es lo que permite que ninguna columna cambie de significado según
// dónde esté. Lo que sí comparten es cómo se pinta un artículo, una familia o un
// estado, y eso vive aquí para que no se dupliquen ni se separen.
import type { ReactNode } from 'react'
import type { AggColumn, PedidoVentaLinea } from '@/types'
import { ESTADO_COLOR, fmtDate, fmtEur, fmtN } from '../formato'
import { apagadoCls } from './estilos'

/** Una columna de una tabla de líneas.
 *
 *  `agg` sale directo a `SubTotalsRow`: al derivarse el pie de la MISMA lista de
 *  columnas visibles, el total no puede quedar desalineado con su cabecera. Una
 *  columna sin `agg` no suma — y algunas no deben: ver `columnasFacturadas`. */
export interface ColumnaLinea {
  key: string
  etiqueta: string
  /** Clase de ancho para el `<colgroup>`; con `table-fixed` es el mínimo. */
  ancho: string
  alinear?: 'right'
  /** Clave por la que ordena esta columna. Sin ella, no es ordenable. */
  valorOrden?: (l: PedidoVentaLinea) => string | number | undefined
  agg?: AggColumn<PedidoVentaLinea>
  formatoTotal?: (valor: number) => string
  render: (l: PedidoVentaLinea) => ReactNode
}

export interface ContextoCeldas {
  dark: boolean
  fmtArt: (code?: string) => string
  t: (clave: string, opciones?: Record<string, unknown>) => string
  /** ¿Esta fila es la que se está buscando? Se resalta entera. */
  coincide?: ((articulo?: string, descripcion?: string) => boolean) | null
}

// ── Celdas compartidas ───────────────────────────────────────────────────────

export function celdaArticulo(l: PedidoVentaLinea, { dark, fmtArt }: ContextoCeldas) {
  return (
    <div className="max-w-full">
      <div className="font-semibold truncate text-[11px] flex items-center gap-1.5" title={fmtArt(l.articulo)}>
        {fmtArt(l.articulo) || '—'}
        {/* ARMAF es una marca, no una alerta. En un pedido de ARMAF lo llevan
            TODAS las líneas, así que en ámbar relleno se convertía en una
            columna de ruido: aquí va como marca discreta, que es lo que es. */}
        {!!l.esArmaf && (
          <span className={`shrink-0 text-[8px] font-bold px-1 py-0.5 rounded tracking-wider ${
            dark ? 'text-slate-500 bg-white/5' : 'text-slate-500 bg-slate-100'
          }`}>
            ARMAF
          </span>
        )}
      </div>
      <div className={`text-[9px] truncate ${apagadoCls(dark)}`} title={l.descripcion}>
        {l.descripcion}
      </div>
    </div>
  )
}

export function celdaFamilia(l: PedidoVentaLinea, { dark }: ContextoCeldas) {
  return (
    <div className="max-w-full">
      <div className="text-[10px] truncate" title={l.familia}>{l.familia ?? '—'}</div>
      {l.subfamilia && (
        <div className={`text-[9px] truncate ${apagadoCls(dark)}`} title={l.subfamilia}>
          {l.subfamilia}
        </div>
      )}
    </div>
  )
}

export function celdaEstado(l: PedidoVentaLinea, { dark, t }: ContextoCeldas) {
  if (!l.estadoLinea) return '—'
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
      ESTADO_COLOR[l.estadoLinea] ?? (dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-500')
    }`}>
      {t('salesOrders.status.' + l.estadoLinea, { defaultValue: l.estadoLinea })}
    </span>
  )
}

export function celdaImporte(valor?: number | null, moneda?: string, dark = false) {
  if (!valor) return '—'
  return (
    <>
      {fmtEur(valor)}
      {moneda && moneda !== 'EUR' && (
        <div className={`text-[9px] font-normal ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          {moneda}
        </div>
      )}
    </>
  )
}

export const celdaPrecio = (l: PedidoVentaLinea) =>
  l.precioUnitario
    ? l.precioUnitario.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
    : '—'

export const celdaFecha = (iso?: string) => fmtDate(iso)

export const celdaCantidad = (n?: number | null) => fmtN(n ?? 0)

// ── Columnas que las dos tablas comparten ────────────────────────────────────

/** Identificación de la línea: las mismas tres columnas en los dos grupos, para
 *  que la mirada no tenga que recolocarse al pasar de una tabla a la otra. */
export function columnasIdentidad(ctx: ContextoCeldas): ColumnaLinea[] {
  const { t } = ctx
  return [
    {
      key: 'numLinea',
      etiqueta: t('salesOrders.line.lineNum'),
      ancho: 'w-[48px]',
      valorOrden: l => l.numLinea,
      render: l => (
        <span className={apagadoCls(ctx.dark)}>{l.numLinea ?? '—'}</span>
      ),
    },
    {
      key: 'articulo',
      etiqueta: t('salesOrders.line.article'),
      ancho: 'w-[240px]',
      valorOrden: l => ctx.fmtArt(l.articulo),
      render: l => celdaArticulo(l, ctx),
    },
    {
      key: 'familia',
      etiqueta: t('salesOrders.line.family'),
      ancho: 'w-[140px]',
      valorOrden: l => l.familia,
      render: l => celdaFamilia(l, ctx),
    },
  ]
}

/** Cola común: unidad, estado y las dos fechas. */
export function columnasCola(ctx: ContextoCeldas): ColumnaLinea[] {
  const { t } = ctx
  return [
    {
      key: 'um',
      etiqueta: t('salesOrders.line.um'),
      ancho: 'w-[56px]',
      valorOrden: l => l.um,
      render: l => <span className={apagadoCls(ctx.dark)}>{l.um ?? '—'}</span>,
    },
    {
      key: 'estadoLinea',
      etiqueta: t('salesOrders.line.status'),
      ancho: 'w-[120px]',
      valorOrden: l => l.estadoLinea,
      render: l => celdaEstado(l, ctx),
    },
    {
      key: 'fechaEntrega',
      etiqueta: t('salesOrders.line.deliveryDate'),
      ancho: 'w-[100px]',
      alinear: 'right',
      valorOrden: l => l.fechaEntrega,
      render: l => celdaFecha(l.fechaEntrega),
    },
    {
      key: 'fechaEnvio',
      etiqueta: t('salesOrders.line.shipDate'),
      ancho: 'w-[100px]',
      alinear: 'right',
      valorOrden: l => l.fechaEnvio,
      render: l => celdaFecha(l.fechaEnvio),
    },
  ]
}
